import Paper from "@mui/material/Paper";
import axios from "axios";
import moment from "moment-timezone";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import { useState } from "react";
import { MAX_TABLE_ROWS, qritColumns, stpColumns } from "../data/constants";
import { app } from "../signals/AppSignal";
import { parsePlan } from "../utils/StringUtil";

interface TypeTransactions {
  id: number;
  actionType: number;
  dateCreated: string;
  referenceId: string;
  accountId: string;
  userName: string;
  fileName: string;
  format: string;
  status: string;
  planId: string | null;
  boardId: string;
  permissions: string;
  boardKind: string;
  owners: string;
  extractData: string;
  note: string;
  clientTrackerId?: string;
  postalCode?: string;
}

const Transactions = () => {
  useSignal(0);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [transactions, setTransactions] = useState<TypeTransactions[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagnition, setPagnation] = useState<any>({});

  const [paginationModel, setPaginationModel] = useState<{
    page: number;
    pageSize: number;
  }>({ page: 0, pageSize: 50 });

  const rowCount = transactions.length || 0;
  const pageCount = Math.ceil(rowCount / paginationModel.pageSize);
  const startIndex = paginationModel.page * paginationModel.pageSize + 1;
  const endIndex = Math.min(
    (paginationModel.page + 1) * paginationModel.pageSize,
    rowCount,
  );

  const getTablePage = (totalPages: number) => {
    const pages: React.ReactNode[] = [];
    const currentPage = pagnition.currentPage;

    // Controls how many pages to show before and after the current page
    const neighbors = 2;

    for (let i = 1; i <= totalPages; i++) {
      // Always show the first page, the last page, and pages immediately surrounding the current page
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - neighbors && i <= currentPage + neighbors)
      ) {
        const isCurrent = currentPage === i;
        pages.push(
          <li key={i} className="page-item">
            <button
              disabled={isCurrent}
              className={
                isCurrent
                  ? "page-link text-white fw-bold active"
                  : "page-link text-dark"
              }
              onClick={() => getTransactions(i)}
            >
              {i}
            </button>
          </li>,
        );
      }
      // Add Ellipsis if we are skipping chunks of pages
      else if (
        i === currentPage - neighbors - 1 ||
        i === currentPage + neighbors + 1
      ) {
        pages.push(
          <li key={`ellipsis-${i}`} className="page-item disabled">
            <span className="page-link text-muted bg-light border-0">...</span>
          </li>,
        );
      }
    }

    return pages;
  };

  const handlePageSizeChange = (event: { target: { value: string } }) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      page: 0, // Reset to first page if pageSize changes
    }));
  };

  const handleNext = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.min(prev.page + 1, pageCount - 1),
    }));
  };

  const handlePrev = () => {
    setPaginationModel((prev) => ({
      ...prev,
      page: Math.max(prev.page - 1, 0),
    }));
  };

  const getColumnType = (app: string) => {
    switch (app) {
      case "qrit":
        return qritColumns;
      case "stp":
        return stpColumns;
      default:
        return qritColumns;
    }
  };

  const convertUTCToLocal = (utcDateStr: string) => {
    return moment
      .utc(utcDateStr) // Parse as UTC
      .tz(moment.tz.guess())
      .format("MMMM D, YYYY, HH:mm:ss");
  };

  const getTransactions = async (page: number = 1) => {
    setLoading(true);
    setTransactions([]);
    axios
      .get(
        `/api/v1/activities/${app.value}/transactions?limit=${MAX_TABLE_ROWS}&page=${page}`,
      )
      .then((response) => {
        setPagnation(response.data.pagination);
        setPaginationModel({
          page: response.data.pagination.currentPage,
          pageSize: response.data.pagination.pageSize,
        });

        let transactionsList = response.data.data as TypeTransactions[];

        if (transactionsList.length > paginationModel.pageSize) {
          transactionsList = transactionsList.slice(
            0,
            paginationModel.pageSize,
          );
        }

        if (
          typeof transactionsList === "object" &&
          Array.isArray(transactionsList)
        ) {
          transactionsList = transactionsList?.map((list, index) => ({
            ...list,
            id: index + 1,
            planId: parsePlan(app.value, list.planId),
            ...(app.value === "stp" && {
              postalCode: list.clientTrackerId
                ? list.clientTrackerId.split("-")[2]
                : "N/A",
            }),
          }));
          setTransactions(transactionsList);
        } else {
          setTransactions([]);
        }
      })
      .catch((error) => {
        console.log(error);
        setTransactions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getAppValue = () => {
    switch (app.value) {
      case "stp":
        setColumnVisibilityModel({
          referenceId: false,
        });
        break;
      case "qrit":
        setColumnVisibilityModel({
          planId: false,
          clientTrackerId: false,
        });
        break;
      default:
        setColumnVisibilityModel({
          note: false,
          clientTrackerId: false,
        });
        break;
    }
  };

  useSignalEffect(() => {
    getTransactions();
    getAppValue();
  });

  return (
    <div className="row mt-2">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-lg-flex">
              <div>
                <h5 className="mb-0">Transactions</h5>
              </div>
              <div className="ms-auto my-auto mt-lg-0 mt-4">
                <div className="ms-auto my-auto">
                  <button
                    type="button"
                    className="btn btn-icon-only btn-rounded btn-secondary mb-0 ms-2 btn-sm d-flex align-items-center justify-content-center ms-auto"
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    title="Refresh"
                    onClick={() => getTransactions()}
                  >
                    <i className="material-symbols-rounded text-md">sync</i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="mb-4 row align-items-center compact-controls">
              <div className="col-auto d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm select-list"
                  onChange={handlePageSizeChange}
                  value={paginationModel.pageSize}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="40">40</option>
                  <option value="50">50</option>
                </select>
                <span>entries per page</span>
              </div>

              <div className="col d-flex align-items-center gap-2">
                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handlePrev}
                  disabled={paginationModel.page === 0}
                >
                  Previous
                </button>

                <button
                  className="m-0 btn btn-sm btn-outline-info"
                  onClick={handleNext}
                  disabled={paginationModel.page >= pageCount - 1}
                >
                  Next
                </button>

                <div
                  className="d-flex align-items-center"
                  style={{ height: "100%" }}
                >
                  <span style={{ lineHeight: 1 }}>
                    Page {startIndex}–{endIndex} of {rowCount}
                  </span>
                </div>
              </div>

              <div className="col d-flex align-items-center justify-content-end">
                <nav aria-label="Table navigation">
                  <ul className="pagination pagination-sm mb-0 align-items-center">
                    {/* Previous Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={pagnition?.currentPage === 1}
                        onClick={() => {
                          const prev = pagnition?.currentPage - 1;
                          if (prev >= 1) getTransactions(prev);
                        }}
                      >
                        <span aria-hidden="true">&laquo;</span>
                      </button>
                    </li>

                    {/* Dynamic Page List */}
                    {getTablePage(pagnition?.totalPages || 0)}

                    {/* Next Button */}
                    <li className="page-item">
                      <button
                        className="page-link text-dark"
                        disabled={
                          pagnition?.currentPage === pagnition?.totalPages
                        }
                        onClick={() => {
                          const next = pagnition?.currentPage + 1;
                          if (next <= pagnition?.totalPages)
                            getTransactions(next);
                        }}
                      >
                        <span aria-hidden="true">&raquo;</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <Paper sx={{ height: "550px", width: "100%" }}>
              <DataGrid
                loading={loading}
                rows={transactions}
                columns={getColumnType(app.value)}
                hideFooter={true}
                slots={{ toolbar: GridToolbar }}
                initialState={{ pagination: { paginationModel } }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel: any) =>
                  setColumnVisibilityModel(newModel)
                }
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) =>
                  setPaginationModel(newModel)
                }
                pageSizeOptions={[10, 20, 30, 40, 50]}
                localeText={{ noRowsLabel: "No data available to display" }}
                disableRowSelectionOnClick
                sx={{ border: 0 }}
              />
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
